import { defineStore } from "pinia";
import {
  DispatchMission,
  DispatchTemplate,
  MateDispatch,
  MateDispatchMember,
  MateEquipment,
  MateJournal,
  MateMail,
} from "@/types/entity";
import {
  DispatchType,
  EquipmentRarity,
  MateDispatchStatusRequest,
} from "@/enums/mission";
import {
  ApiGetDispatchMission,
  ApiGetDispatchMissionPage,
  ApiCreateMateDispatch,
  ApiGetMateDispatch,
  ApiGetMateEquipmentPage,
  ApiGetMateEquipment,
  ApiDeleteMateEquipment,
  ApiGetMateJournalPage,
  ApiSettleMateDispatch,
  ApiPutMateJournal,
  ApiExpandMateEquipmentInventory,
  ApiUpdateMateDispatch,
  ApiCancelInvitation,
  ApiDeleteMateJournal,
  ApiMateDispatchParams,
  ApiRetryDispatchMission,
  ApiDecomposeMateEquipment,
  ApiOpenMateEquipmentChest,
  ApiPutMateEquipment,
  ApiDecomposeManyMateEquipment,
} from "@/apis/dispatchMission";
import { useMate } from "./mate";
import { ApiGetMateMailPage } from "@/apis/interaction";
import { MateMailType } from "@/enums/mateEnums";
import { sessionStorageSet } from "@/tools/sessionStorage";
import { useNotification } from "./notification";

type Tab =
  | "all"
  | MateDispatchStatusRequest.Preparing
  | MateDispatchStatusRequest.Dispatched
  | MateDispatchStatusRequest.Returned
  | MateDispatchStatusRequest.Settled
  | MateDispatchStatusRequest.Canceled;

export const useMission = defineStore("mission", {
  state: () => {
    return {
      missionList: [] as DispatchMission[],
      mateDispatchList: [] as Array<number>,
      mateDispatchMap: {} as Record<number, MateDispatch>,
      mateEquipmentList: [] as Array<MateEquipment>,
      mateEquipmentMap: {} as Record<number, MateEquipment>,
      mateJournalList: [] as Array<MateJournal>,
      mateJournalMap: {} as Record<number, MateJournal>,
      mateMailList: [] as Array<MateMail>,
      mateMailMap: {} as Record<number, MateMail>,

      // MissionHome
      activeTab: "all" as Tab,
      listMap: {
        all: [],
        [MateDispatchStatusRequest.Preparing]: [],
        [MateDispatchStatusRequest.Dispatched]: [],
        [MateDispatchStatusRequest.Returned]: [],
        [MateDispatchStatusRequest.Settled]: [],
        [MateDispatchStatusRequest.Canceled]: [],
      } as Record<string, number[]>,
    };
  },
  getters: {
    missionMap(state) {
      return state.missionList.reduce((acc, mission) => {
        acc[mission.id] = mission;
        return acc;
      }, {} as Record<number, DispatchMission>);
    },
    uuid2MateDispatchMap(state) {
      return Object.values(state.mateDispatchMap).reduce((acc, dispatch) => {
        acc[dispatch.uuid] = dispatch;
        return acc;
      }, {} as Record<string, MateDispatch>);
    },
    // mateDispatchMap(state) {
    //   return state.mateDispatchList.reduce((acc, dispatch) => {
    //     acc[dispatch.id] = dispatch;
    //     return acc;
    //   }, {} as Record<number, MateDispatch>);
    // },
  },
  actions: {
    getDispatchById(id: number) {
      return this.mateDispatchMap[id];
    },
    async getMissionList(force = false) {
      if (!force && this.missionList.length > 0) {
        return this.missionList;
      }
      const res = await ApiGetDispatchMissionPage();
      this.missionList = res.page.data;
    },
    async getMission(uuid: string) {
      const existingMission = this.missionList.find(
        (mission) => mission.uuid === uuid
      );
      if (existingMission) return existingMission;
      const res = await ApiGetDispatchMission(uuid);
      this.missionList.push(res.item);
      return res.item;
    },
    isMyMission(uuid: string): boolean {
      return this.missionList.some((mission) => mission.uuid === uuid);
    },
    isMyMissionById(id: number): boolean {
      return this.missionList.some((mission) => mission.id === id);
    },

    async getMateDispatch(uuid: string, mateId?: number) {
      const res = await ApiGetMateDispatch(uuid, mateId);
      this.mateDispatchMap[res.item.id] = res.item;
      return res.item;
    },

    async retryDispatchMission(
      id: number,
      data?: { memberzz?: MateDispatchMember[] }
    ) {
      const res = await ApiRetryDispatchMission(id, data);
      sessionStorageSet("mission-home:reload", true);
      return res.item;
    },

    async cancelInvitation(id: number) {
      const res = await ApiCancelInvitation(id);
      sessionStorageSet("mission-home:reload", true);
      return res.item;
    },

    async createMateDispatch(dispatch: ApiMateDispatchParams) {
      const res = await ApiCreateMateDispatch(dispatch);
      sessionStorageSet("mission-home:reload", true);
      return res.item;
    },

    async updateMateDispatch(id: number, dispatch: ApiMateDispatchParams) {
      const res = await ApiUpdateMateDispatch(id, dispatch);
      sessionStorageSet("mission-home:reload", true);
      return res.item;
    },

    async settleMateDispatch(id: number, mateId?: number) {
      const res = await ApiSettleMateDispatch(id, { mateId });
      this.getMateJournalList(1);
      this.getMateEquipmentList(true);

      const notificationStore = useNotification();

      if (notificationStore.interactionSummary) {
        notificationStore.initSummaryCount();
      }

      sessionStorageSet("mission-home:reload", true);
      return res.item;
    },

    // MateEquipment actions
    async getMateEquipmentList(force = false) {
      if (!force && this.mateEquipmentList.length > 0) {
        return this.mateEquipmentList;
      }
      const res = await ApiGetMateEquipmentPage({
        filter: { mateId: useMate().userMate.id! },
      });
      this.mateEquipmentList = res.page.data;
      this.mateEquipmentMap = this.mateEquipmentList.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {} as Record<number, MateEquipment>);
      return this.mateEquipmentList;
    },

    totalCostPower(dispatchMission: Partial<MateDispatch>) {
      if (dispatchMission.type === DispatchType.Mission) {
        const {
          carriedJournalzz,
          carriedMailzz,
          carriedDispatchzz,
          carriedMemoryzz,
        } = dispatchMission;
        return (
          dispatchMission.duration! +
          (carriedJournalzz || []).length * 0.5 +
          (carriedMailzz || []).length * 0.5 +
          (carriedDispatchzz || []).length * 0.5 +
          (carriedMemoryzz || []).length * 0.5
        );
      } else {
        return 4;
      }
    },

    async expandMateEquipmentInventory() {
      const res = await ApiExpandMateEquipmentInventory();
      return res.item;
    },

    async getMateEquipment(uuid: string) {
      const existingEquipment = this.mateEquipmentList.find(
        (equipment) => equipment.uuid === uuid
      );
      if (existingEquipment) return existingEquipment;
      const res = await ApiGetMateEquipment(uuid);
      this.mateEquipmentList.push(res.item);
      return res.item;
    },

    async decomposeMateEquipment(id: number) {
      const res = await ApiDecomposeMateEquipment(id);
      this.mateEquipmentList = this.mateEquipmentList.filter(
        (equipment) => equipment.id !== id
      );
      return res.item;
    },

    async putMateEquipment(id: number, request: { isLocked?: boolean }) {
      const res = await ApiPutMateEquipment(id, request);
      return res.item;
    },

    async decomposeManyMateEquipment(rarities: EquipmentRarity[]) {
      const res = await ApiDecomposeManyMateEquipment(rarities);
      this.getMateEquipmentList(true);
      return res.data;
    },

    async openMateEquipmentChest(id: number) {
      const res = await ApiOpenMateEquipmentChest(id);

      this.mateEquipmentList = this.mateEquipmentList.filter(
        (equipment) => equipment.id !== id
      );

      return res.data;
    },

    async deleteMateEquipment(id: number) {
      await ApiDeleteMateEquipment(id);
      this.mateEquipmentList = this.mateEquipmentList.filter(
        (equipment) => equipment.id !== id
      );
    },

    async putMateJournal(id: number, request: Partial<MateJournal>) {
      const res = await ApiPutMateJournal(id, request);
      return res.item;
    },

    async deleteMateJournal(id: number) {
      await ApiDeleteMateJournal(id);
    },

    // MateJournal actions
    async getMateJournalList(
      page = 1,
      mateId?: number,
      order: "desc" | "asc" = "desc",
      coinRankId?: number
    ) {
      if (!mateId) mateId = useMate().userMate.id!;
      const res = await ApiGetMateJournalPage({
        filter: { mateId, coinRankId },
        pagination: { page, perPage: 5 },
        sort: { field: "id", order },
      });

      if (page === 1) this.mateJournalList = [];
      res.page.data.forEach((journal) => {
        this.mateJournalMap[journal.id] = journal;
        this.mateJournalList.push(journal);
      });
      return res.page.data;
    },

    // MateMail actions
    async getMateMailList(
      page = 1,
      mateId?: number,
      otherMateId?: number,
      order: "desc" | "asc" = "desc"
    ) {
      if (!mateId) mateId = useMate().userMate.id!;
      const res = await ApiGetMateMailPage({
        filter: {
          mateId,
          type: MateMailType.Threadbox,
          otherMateIds: otherMateId ? [otherMateId] : undefined,
        },
        pagination: { page, perPage: 5 },
        sort: { field: "dtUpdate", order },
      });

      if (page === 1) this.mateMailList = [];
      res.page.data.forEach((mail) => {
        this.mateMailMap[mail.id] = mail;
        this.mateMailList.push(mail);
      });
      return res.page.data;
    },
  },
});
